'use client';

/**
 * Donut visualization for a Q/V/M/Composite score in [-3, +3] z-score range.
 * Maps the score to ring fill % (0% at -3, 50% at 0, 100% at +3) and color
 * (green at high, amber at neutral, red at low).
 */
export function ScoreRing({
  score, label, rank, peerCount, accent, onClick, verbal, verbalColor,
}: {
  score: number | null;
  label: string;
  rank?: number | null;
  peerCount?: number;
  accent?: string;
  onClick?: () => void;
  verbal?: string;
  verbalColor?: string;
}) {
  const size = 92;
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = score == null ? 0 : Math.max(-3, Math.min(3, score));
  const pct = (clamped + 3) / 6;
  const dashOffset = c * (1 - pct);
  const color = score == null ? '#475569'
    : score >= 1.5 ? '#22c55e'
    : score >= 0.5 ? '#4ade80'
    : score >= -0.5 ? '#eab308'
    : score >= -1.5 ? '#f97316'
    : '#ef4444';
  const ringColor = accent ?? color;
  return (
    <div
      className={`flex flex-col items-center ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      title={onClick ? 'Click for quality attribution' : undefined}
    >
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="#1e293b" strokeWidth={stroke} fill="none" />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={ringColor}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={c}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 400ms ease, stroke 300ms ease' }}
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <div style={{ color, fontSize: 20, fontWeight: 700, lineHeight: 1 }}>
            {score == null ? '--' : (score >= 0 ? '+' : '') + score.toFixed(2)}
          </div>
          {rank != null && peerCount != null && (
            <div style={{ color: '#64748b', fontSize: 9, marginTop: 2 }}>
              #{rank}/{peerCount}
            </div>
          )}
        </div>
      </div>
      <div
        className="text-[10px] font-semibold uppercase tracking-wider mt-1.5"
        style={{ color: accent ?? '#e2e8f0' }}
      >
        {label}
      </div>
      {verbal && (
        <div
          className="text-[9px] font-semibold lowercase tracking-wide mt-0.5"
          style={{ color: verbalColor ?? '#94a3b8' }}
          title="V > 0 means cheap (good); V < 0 means expensive (bad). Verbal reading reflects this."
        >
          {verbal}
        </div>
      )}
    </div>
  );
}

export function TrendArrow({ direction, size = 'sm' }: { direction: 'up' | 'down' | 'flat'; size?: 'sm' | 'xs' | 'md' }) {
  const sz = size === 'md' ? 16 : size === 'sm' ? 14 : 10;
  if (direction === 'up') return (
    <svg width={sz} height={sz} viewBox="0 0 12 12" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 2 }}>
      <path d="M6 10V2M6 2L2.5 6M6 2L9.5 6" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  if (direction === 'down') return (
    <svg width={sz} height={sz} viewBox="0 0 12 12" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 2 }}>
      <path d="M6 2V10M6 10L2.5 6M6 10L9.5 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  return (
    <svg width={sz} height={sz} viewBox="0 0 12 12" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 2 }}>
      <path d="M2 6H10" stroke="#64748b" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
